import React from 'react';

function Home() {
  return (
    <div className="page-container">
      <div className="hero-section">
        <h1 className="hero-title">Rob Higareda</h1>
        <p className="hero-subtitle">Cloud Infrastructure & Security Leader | CISSP</p>
      </div>

      {/* Stats Section */}
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-number">24+</span>
          <span className="stat-label">Years Experience</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">8</span>
          <span className="stat-label">AWS Certifications</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">Top Secret</span>
          <span className="stat-label">Security Clearance</span>
        </div>
      </div>

      <div className="content-section">
        {/* About Section */}
        <div className="card">
          <h2 className="section-title">About Me</h2>
          <p className="text-content">
            I'm a Cloud Architect and Security Leader with over 24 years of experience 
            spanning enterprise infrastructure, cloud security, and large-scale compliance frameworks. 
            I specialize in bridging complex regulatory mandates with advanced cloud engineering, 
            having architected foundational frameworks like the AWS DISA SCCA and FedRAMP Rev5 
            Secure Configuration Guidance that define how the public sector adopts cloud securely.
          </p>
          <p className="text-content">
            As a former Amazon Bar Raiser, CISSP since 2009, and holder of an active Top Secret clearance, 
            I bring deep expertise in security architecture, AI-driven compliance tooling, and 
            cross-organizational technical leadership to every engagement.
          </p>
          <div className="badge-container">
            <span className="badge">Cloud Security</span>
            <span className="badge">AWS Architecture</span>
            <span className="badge">FedRAMP & DISA SCCA</span>
            <span className="badge">AI/ML & Agentic AI</span>
            <span className="badge">Compliance Frameworks</span>
            <span className="badge">Microsoft Workloads</span>
          </div>
        </div>

        {/* Core Services */}
        <div className="card">
          <h2 className="section-title">What I Do</h2>
          <div className="icon-grid">
            <div className="icon-card">
              <span className="icon-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  <circle cx="12" cy="16" r="1"/>
                </svg>
              </span>
              <h3 className="icon-card-title">Security & Compliance Architecture</h3>
              <p className="icon-card-description">
                Designing secure cloud architectures for government and enterprise at scale. 
                Author of AWS DISA SCCA framework and FedRAMP Rev5 guidance used by DoD 
                and regulated entities worldwide.
              </p>
            </div>
            <div className="icon-card">
              <span className="icon-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
                  <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
                </svg>
              </span>
              <h3 className="icon-card-title">Cloud Migration & Modernization</h3>
              <p className="icon-card-description">
                Leading large-scale migrations of thousands of workloads to AWS. 
                Delivered $3M+ in annual cost optimization through architectural 
                improvements and strategic modernization.
              </p>
            </div>
            <div className="icon-card">
              <span className="icon-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </span>
              <h3 className="icon-card-title">AI & Automation</h3>
              <p className="icon-card-description">
                Building AI-driven compliance tooling, agentic AI workflows, and 
                generative AI solutions. Developed AI documentation analysis engines 
                adopted by AWS service teams.
              </p>
            </div>
            <div className="icon-card">
              <span className="icon-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </span>
              <h3 className="icon-card-title">Technical Leadership & Enablement</h3>
              <p className="icon-card-description">
                Mentoring engineers to principal-level roles, hosting AWS technical 
                content on Twitch.tv, and speaking at AWS re:Invent and DC Summit 
                on security and compliance topics.
              </p>
            </div>
          </div>
        </div>

        {/* Technical Expertise */}
        <div className="card">
          <h2 className="section-title">Technical Expertise</h2>
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h3 className="timeline-title">Cloud Security & Compliance</h3>
                <p className="timeline-subtitle">CISSP | 8x AWS Certified | Top Secret Clearance</p>
                <p className="timeline-description">
                  Architecting secure cloud solutions for DoD, Federal, and enterprise customers. 
                  Author of the AWS DISA SCCA framework and FedRAMP Rev5 guidance. Deep expertise 
                  in FedRAMP, PCI DSS, SOC 2, and NIST frameworks.
                </p>
                <div className="badge-container">
                  <span className="badge">CISSP</span>
                  <span className="badge">FedRAMP</span>
                  <span className="badge">DISA SCCA</span>
                  <span className="badge">PCI DSS</span>
                  <span className="badge">SOC 2</span>
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h3 className="timeline-title">AWS Architecture & Migration</h3>
                <p className="timeline-subtitle">7+ Years Deep AWS Expertise</p>
                <p className="timeline-description">
                  Leading large-scale migrations of thousands of workloads to AWS. 
                  Expertise across EC2, EBS, RDS, EFS, Storage Gateways, VPC, Microsoft AD, 
                  FSx for Windows, and VMware Cloud on AWS.
                </p>
                <div className="badge-container">
                  <span className="badge aws">EC2</span>
                  <span className="badge aws">FSx</span>
                  <span className="badge aws">VPC</span>
                  <span className="badge aws">CloudFormation</span>
                  <span className="badge aws">VMware Cloud</span>
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h3 className="timeline-title">AI & Automation</h3>
                <p className="timeline-subtitle">Agentic AI | RAG | LLMs</p>
                <p className="timeline-description">
                  Building AI-driven tooling for compliance automation, documentation analysis, 
                  and engineering workflows. Experience with Anthropic Claude, Amazon Nova models, 
                  and agentic AI patterns in AWS services.
                </p>
                <div className="badge-container">
                  <span className="badge">Generative AI</span>
                  <span className="badge">Agentic AI</span>
                  <span className="badge">RAG</span>
                  <span className="badge">CI/CD</span>
                </div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h3 className="timeline-title">Microsoft & Infrastructure</h3>
                <p className="timeline-subtitle">20+ Years Windows & Linux</p>
                <p className="timeline-description">
                  Deep expertise in Windows Server, Active Directory, SQL Server, Hyper-V, 
                  and Linux server technologies. Experienced with VMware ESXi virtualization 
                  and hybrid cloud architectures.
                </p>
                <div className="badge-container">
                  <span className="badge">Windows Server</span>
                  <span className="badge">Active Directory</span>
                  <span className="badge">SQL Server</span>
                  <span className="badge">VMware ESXi</span>
                  <span className="badge">Linux</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Services */}
        <div className="card">
          <h2 className="section-title">Beyond the Day Job</h2>
          <p className="text-content">
            Outside of cloud security and architecture, I'm a passionate photographer and 
            creative professional. I bring the same attention to detail and quality to my 
            creative work that defines my technical career.
          </p>
          <div className="contact-links">
            <a href="https://www.megapixelphotos.com" target="_blank" rel="noopener noreferrer" className="contact-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              Photography Portfolio
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
